import { Injectable } from '@angular/core';
import { LogEntry } from 'src/app/_model/log-entry';
import { LoggingDaoService } from '../logging-dao.service';
import { DbPersistenceService } from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingDbTableDefinition } from './logging-db-table-definition';
import { SQLiteDBConnection } from '@capacitor-community/sqlite';
import { LogInfos } from '../../logic/logging.service';
import { Capacitor } from '@capacitor/core';

/**
 * maximum age / keeping duration of log entries
 */
const LOG_DURATION = 4 * 24 * 60 * 60 * 1000; // 96 hours

/**
 * Web only: log entries are buffered and written in batches. With jeep-sqlite autoSave every
 * single write exports the complete log DB into IndexedDB - one export per log line ran the
 * browser out of memory ("Array buffer allocation failed") and broke the DB handle for the
 * rest of the session.
 */
const WEB_LOG_FLUSH_INTERVAL_MS = 2000;
const WEB_LOG_MAX_BATCH_SIZE = 500;
// upper bound while the log DB does not answer, so the buffer cannot eat up the memory itself
const WEB_LOG_MAX_PENDING = 5000;

/**
 * DataAccessObject for Log Entries
 */
@Injectable()
export class LoggingDbDaoService implements LoggingDaoService {
  // Private Member
  private logDb: Promise<SQLiteDBConnection>;
  private isLogDbLoaded = false;

  private temporaryLogs: LogEntry[] = [];

  // Web only, see WEB_LOG_FLUSH_INTERVAL_MS
  private readonly batchWrites = !Capacitor.isNativePlatform();
  private pendingLogs: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout>;
  private flushChain: Promise<void> = Promise.resolve();
  private flushQueued = false;

  // LifeCycle
  constructor(
    private dbPersistenceService: DbPersistenceService,
    private loggingTableDefinition: LoggingDbTableDefinition
  ) {
    this.logDb = this.initDb();

    if (this.batchWrites) {
      // best effort: write what is buffered before the page goes away
      window.addEventListener('pagehide', () => this.flushPendingLogs());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flushPendingLogs();
        }
      });
    }
  }

  private async initDb(): Promise<SQLiteDBConnection> {
    try {
      const logDb = await this.dbPersistenceService.getLogDb();
      await this.purgeLogs(logDb, LOG_DURATION);
      this.isLogDbLoaded = true;
      return logDb;
    } catch (error) {
      console.warn(this.constructor.name, '- Error initializing logdb', error);
      return Promise.reject('Error initializing logdb');
    }
  }

  // LoggingDao
  public async clearLogs(): Promise<void> {
    let db = await this.logDb;
    await this.purgeLogs(db, 60 * 60 * 1000);
  }

  public async saveLog(logEntry: LogEntry): Promise<void> {
    if (this.batchWrites) {
      this.pendingLogs.push(logEntry);
      if (this.pendingLogs.length > WEB_LOG_MAX_PENDING) {
        this.pendingLogs.splice(0, this.pendingLogs.length - WEB_LOG_MAX_PENDING);
      }
      this.scheduleFlush();
      return;
    }

    try {
      if (!this.isLogDbLoaded) {
        // console.log(this.constructor.name, 'Add Log to temporary logs');
        this.temporaryLogs.push(logEntry);
        return;
      }
      let db = await this.logDb;
      if (this.temporaryLogs.length > 0) {
        console.log(
          this.constructor.name,
          'Add all temporary logs',
          this.temporaryLogs.length
        );
        const logsToWrite = this.temporaryLogs;
        this.temporaryLogs = [];

        await this.dbPersistenceService.executeStatements(
          db,
          logsToWrite
            .flatMap((logEntry) => {
              return this.splitLogEntry(logEntry)
            })
            .map((logEntry) => {
              return {
                statement: `INSERT INTO ${this.loggingTableDefinition.tableName} (level, timestamp, message, stacktrace) VALUES (?, ?, ?, ?);`,
                parameters: [
                  logEntry.level,
                  logEntry.timestamp,
                  logEntry.message,
                  logEntry.stacktrace ? logEntry.stacktrace : '',
                ],
              };
            }),
          Capacitor.isNativePlatform() ? undefined : false
        );
      }

      const entriesToSave = this.splitLogEntry(logEntry);
      for (const entry of entriesToSave) {
        await this.dbPersistenceService.executeStatement(db, {
          statement:
            'INSERT INTO ' + this.loggingTableDefinition.tableName +
            '(level, timestamp, message, stacktrace) VALUES (?, ?, ?, ?);',
          parameters: [
            entry.level,
            entry.timestamp,
            entry.message,
            entry.stacktrace ?? '',
          ],
        }, Capacitor.isNativePlatform() ? undefined : false);
      }
    } catch (error) {
      console.log('Error saving logs from db', error);
    }
  }

  public async loadLogs(): Promise<LogEntry[]> {
    await this.flushPendingLogs();
    try {
      let db = await this.logDb;
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT level, timestamp, message, stacktrace FROM ' +
          this.loggingTableDefinition.tableName +
          ';',
        parameters: null,
      });

      let logs: LogEntry[] = [];
      for (let i = 0; i < data.values.length; i++) {
        let item = data.values[i];
        logs.push(item);
      }

      return logs;
    } catch (error) {
      console.log('Error reading logs from db', error);
      return undefined;
    }
  }

  public async loadLogsInfo(): Promise<LogInfos> {
    await this.flushPendingLogs();
    try {
      let db = await this.logDb;
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT COUNT(*) as amount, MIN(id) as firstid FROM ' +
          this.loggingTableDefinition.tableName +
          ';',
        parameters: null,
      });

      return {
        amount: data.values[0]['amount'],
        firstId: data.values[0]['firstid'],
      };
    } catch (error) {
      console.log('Error reading logs from db', error);
      return undefined;
    }
  }

  public async loadLogChunk(
    startingIndex: number,
    chunkSize: number
  ): Promise<LogEntry[]> {
    await this.flushPendingLogs();
    try {
      let db = await this.logDb;
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 
          `SELECT id, level, timestamp, message, stacktrace  FROM ${this.loggingTableDefinition.tableName} WHERE id >= ${startingIndex} ORDER BY id ASC LIMIT ${chunkSize};`,
        parameters: null,
      });

      let logs: LogEntry[] = [];
      for (let i = 0; i < data.values.length; i++) {
        let item = data.values[i];
        logs.push(item);
      }

      return logs;
    } catch (error) {
      throw error
    }
  }

  // Private Functions
  private scheduleFlush() {
    if (this.pendingLogs.length >= WEB_LOG_MAX_BATCH_SIZE) {
      this.flushPendingLogs();
    } else if (this.flushTimer == undefined) {
      this.flushTimer = setTimeout(
        () => this.flushPendingLogs(),
        WEB_LOG_FLUSH_INTERVAL_MS
      );
    }
  }

  /**
   * Writes all buffered entries with a single executeSet, i.e. a single export to IndexedDB.
   * Flushes are chained so batches are written in order and never concurrently.
   */
  private flushPendingLogs(): Promise<void> {
    if (this.flushTimer != undefined) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    // one queued flush takes everything pending when it runs - do not queue one per log line
    if (!this.flushQueued) {
      this.flushQueued = true;
      this.flushChain = this.flushChain.then(() => {
        this.flushQueued = false;
        return this.writePendingLogs();
      });
    }
    return this.flushChain;
  }

  private async writePendingLogs(): Promise<void> {
    if (this.pendingLogs.length === 0) {
      return;
    }
    const logsToWrite = this.pendingLogs;
    this.pendingLogs = [];
    try {
      const db = await this.logDb;
      await this.dbPersistenceService.executeBatch(
        db,
        logsToWrite
          .flatMap((logEntry) => this.splitLogEntry(logEntry))
          .map((logEntry) => {
            return {
              statement: `INSERT INTO ${this.loggingTableDefinition.tableName} (level, timestamp, message, stacktrace) VALUES (?, ?, ?, ?);`,
              parameters: [
                logEntry.level,
                logEntry.timestamp,
                logEntry.message,
                logEntry.stacktrace ?? '',
              ],
            };
          })
      );
    } catch (error) {
      // never retry: a failing batch must not pile up in memory
      console.warn(
        this.constructor.name,
        '- Could not write',
        logsToWrite.length,
        'buffered log entries',
        error
      );
    }
  }

  private async purgeLogs(db: SQLiteDBConnection, keepDuration: number) {
    await this.dbPersistenceService.executeStatement(db, {
      statement:
        `DELETE FROM ${this.loggingTableDefinition.tableName} WHERE timestamp < ?;`,
      parameters: [Date.now() - keepDuration],
    });
  }

  private splitLogEntry(
    logEntry: LogEntry,
    maxLength: number = 200000
  ): LogEntry[] {
    const messageLength = logEntry.message?.length ?? 0;
    const stacktraceLength = logEntry.stacktrace?.length ?? 0;

    if (messageLength <= maxLength && stacktraceLength <= maxLength) {
      return [logEntry];
    }

    const chunks: LogEntry[] = [];
    const messageChunks: string[] = [];
    for (let i = 0; i < messageLength; i += maxLength) {
      messageChunks.push(logEntry.message.substring(i, i + maxLength));
    }
    if (messageChunks.length === 0) messageChunks.push('');

    const stacktraceChunks: string[] = [];
    for (let i = 0; i < stacktraceLength; i += maxLength) {
      stacktraceChunks.push(logEntry.stacktrace.substring(i, i + maxLength));
    }

    const totalChunks = Math.max(messageChunks.length, stacktraceChunks.length);
    for (let i = 0; i < totalChunks; i++) {
      chunks.push({
        level: logEntry.level,
        timestamp: logEntry.timestamp,
        message: `[${i + 1}/${totalChunks}] ${messageChunks[i] ?? ''}`,
        stacktrace: stacktraceChunks[i] ?? null,
      });
    }

    return chunks;
  }
}
